import { useRef } from 'react';
import { SortableLayers } from '@/common/presentation/components/layers/SortableLayers';
import type { PriceBookSheetView } from '@/document-render/domain/priceBook/buildPriceBookBySheetView';
import { VISUALLY_HIDDEN_STYLE } from '../priceBookSheetStyles';
import { PriceBookSheetChrome } from './PriceBookSheetChrome';
import type { PriceBookCanvas } from './priceBookEditing';
import { usePriceBookSelectionSync } from './usePriceBookSelectionSync';
import { requireSliceId, usePriceBookSheetReorder } from './usePriceBookSheetReorder';

export interface PriceBookQuoteSheetsProps {
  canvas: PriceBookCanvas;
  /** Every sheet (the reorder domain). */
  allSheets: PriceBookSheetView[];
  /** The sheets printed on this slice. */
  sheets: PriceBookSheetView[];
}

/** The customised by-sheet canvas: selection sync, sheet reorder and its live announcement. */
export function PriceBookQuoteSheets({ canvas, allSheets, sheets }: PriceBookQuoteSheetsProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectLocal = usePriceBookSelectionSync(rootRef, canvas.selectedId, canvas.select);
  const { editing } = canvas;
  const sheetReorder = usePriceBookSheetReorder(
    allSheets.map((sheet) => ({ id: sheet.sheetId, name: sheet.sheetName })),
    (ids) => editing.onReorderPriceBookRows?.('sheet', ids)
  );
  const sliceItems = sheets.map((sheet) => ({ id: sheet.sheetId, name: sheet.sheetName }));
  const fullById = new Map(allSheets.map((sheet) => [sheet.sheetId, sheet]));

  return (
    <div ref={rootRef}>
      <SortableLayers
        items={sliceItems}
        onReorder={(from, to) => sheetReorder.moveById(requireSliceId(sliceItems, from), requireSliceId(sliceItems, to))}
      >
        {sheets.map((sheet) => (
          <PriceBookSheetChrome
            key={sheet.sheetId}
            sheet={sheet}
            fullSheet={fullById.get(sheet.sheetId) ?? sheet}
            editing={editing}
            selectedId={canvas.selectedId}
            onSelect={selectLocal}
            sheetReorder={sheetReorder}
          />
        ))}
      </SortableLayers>
      <div role="status" aria-live="polite" style={VISUALLY_HIDDEN_STYLE}>
        {sheetReorder.announcement}
      </div>
    </div>
  );
}
